<template>
  <vue-form @submit="submit"
            :disabled="isFormDisabled || (cart && !canUpdateCart)"
            :form="form"
            :action="action"
            :status="status">
    <section class="form-section">
      <header>
        <h3>Client</h3>
      </header>
      <div>
        <form-group class="md:w-2/3" field="customer" label="Pour quel client est la commande ?">
          <select-customer :config="selectCustomerConfig"
                           v-model="customer"
                           @select="getCustomerCart"/>
        </form-group>
      </div>
    </section>
    <section class="form-section">
      <header>
        <h3>Panier</h3>
        <p v-if="cart && cart.products.length">
          Votre panier contient <strong v-text="cart.products.length"></strong> produits
          pour une quantité totale de <strong>{{ totalQuantity }}</strong>.
        </p>
        <p v-else>
          Votre panier est vide.
        </p>
      </header>
      <div>
        <div class="py-2 md:p-2 w-full">
          <cart-products v-if="cart && cart.products.length"
                         class="mb-4"
                         :products="cart.products"
                         :disabled="cart && !canUpdateCart"/>
          <div class="flex justify-start w-full">
            <vue-button type="button"
                        class="btn btn-green w-full md:w-auto"
                        v-if="(cart && canUpdateCart) || isCreateForm"
                        @click="handleAddToCart">Ajouter un produit au panier
            </vue-button>
          </div>
        </div>
      </div>
    </section>
    <section class="form-section">
      <header>
        <h3>Retrait</h3>
        <p>
          Le jour, l'heure et la boutique où le client vient récupérer sa commande.
        </p>
      </header>
      <div>
        <form-group class="md:w-1/2" field="pickup_date" label="Jour">
          <vue-date-picker :config="pickupDateConfig"
                           :disabled="isFormDisabled"
                           @on-change="form.errors.clear('pickup_date')"
                           v-model="form.pickup_date"/>
        </form-group>
        <form-group class="md:w-1/2" field="pickup_time" label="Heure">
          <vue-time-picker start="06:00"
                           end="21:00"
                           open-direction="top"
                           :disabled="isFormDisabled"
                           v-model="form.pickup_time"/>
        </form-group>
        <form-group class="md:w-1/2" field="shop" label="Pour quelle boutique est la commande ?" v-if="shops.length">
          <select-shop :config="selectShopConfig"
                       v-model="form.shop"/>
        </form-group>
      </div>
    </section>
    <section class="form-section">
      <header>
        <h3>Notes</h3>
        <p>
          Les notes complémentaires n'apparaissent pas sur les rapports et ne se sont pas visibles par le client.
        </p>
      </header>
      <div>
        <form-group field="note" label="Notes complémentaires">
          <vue-textarea id="note"
                        v-model="form.note"
                        :disabled="isFormDisabled"/>
        </form-group>
      </div>
    </section>
    <section class="form-section">
      <header>
        <h3>Règlement</h3>
        <p>
          L'application ne gère pas l'encaissement mais il vous est sûrement utile de savoir si la commande est réglée
          ou non.
        </p>
      </header>
      <div>
        <form-group field="payment_received" label="Le client a-t-il payé sa commande ?">
          <div class="flex flex-wrap -m-1">
            <vue-radio class="m-1" :label="false" v-model="form.payment_received" :disabled="isFormDisabled">Non, pas
              encore payé
            </vue-radio>
            <vue-radio class="m-1" :label="true" v-model="form.payment_received" :disabled="isFormDisabled">Oui, c'est
              payé
            </vue-radio>
          </div>
        </form-group>
      </div>
    </section>
    <section class="form-section" v-if="isUpdateForm && canDelete">
      <header>
        <h3>Zone de danger</h3>
        <p>
          La commande sera <strong>définitivement supprimée</strong> et ne pourra pas être restaurée.
        </p>
      </header>
      <div>
        <form-group label="Voulez-vous supprimer définitivement cette commande ?">
          <confirm-button @confirmed="destroy"
                          popover-type="popover-top-left sm:popover-right"
                          btn-class="btn-primary btn-outline"
                          btn-confirm-text="Supprimer"
                          :disabled="isFormDisabled"
                          :status="statusDestroy">Supprimer la commande
          </confirm-button>
        </form-group>
      </div>
    </section>
  </vue-form>
</template>

<script>
/* globals axios route */
import Form from 'form-backend-validation';
import {isEqual} from 'lodash';
import form from '../../mixins/form';
import CartProducts from '../carts/CartProducts.vue';
import SelectCustomer from '../customers/CustomerSelect.vue';
import SelectShop from '../shops/ShopSelect.vue';
import ErrorHandler from '../../core/ErrorHandler';

export default {
  mixins: [form],

  components: {
    SelectCustomer,
    SelectShop,
    CartProducts,
  },

  data() {
    return {
      shops: [],
      customer: this.model ? this.model.cart.customer : null,
      form: new Form({
        cart: null,
        shop: null,
        pickup_date: '',
        pickup_time: '',
        payment_received: false,
        note: null,
      }),
    };
  },

  computed: {
    isCartModel() {
      return !!(this.model && isEqual(Object.keys(this.model), ['cart']));
    },

    cart: {
      get() {
        return this.form.cart;
      },
      set(cart) {
        this.form.cart = cart;
      },
    },

    canUpdateCart() {
      if (!this.cart) {
        return false;
      }
      return this.$can(`carts-update-${this.cart.id}`);
    },

    pickupDateConfig() {
      return {
        minDate: 'today',
      };
    },

    selectCustomerConfig() {
      return {
        disabled: this.isUpdateForm || this.isCartModel || this.form.processing,
      };
    },

    selectShopConfig() {
      return {
        disabled: this.isFormDisabled,
      };
    },

    totalQuantity() {
      if (!this.cart) {
        return 0;
      }
      return this.cart.products.reduce(
          (previous, current) => previous + current.quantity,
          0,
      );
    },
  },

  watch: {
    cart(cart) {
      if (cart) {
        this.$authorization.register(`carts-update-${cart.id}`);
      }
    },
  },

  created() {
    Event.listen('cartUpdated', (cart) => {
      if (this.cart && this.cart.id === cart.id) {
        this.refreshCart(cart);
      }
    });
    Event.listen('cartDeleted', (cart) => {
      if (this.cart && this.cart.id === cart.id) {
        this.$modal.close();
      }
    });

    axios
        .get(route('api.shops.index'))
        .then(({data}) => {
          this.shops = data.data;
        })
        .catch(() => {
          this.shops = [];
        });

    if(this.isCreateForm) {
      this.form.shop = window.Pistor.user.shop;
    }
  },

  methods: {
    refreshCart(cart) {
      this.cart = cart;

      if (this.isUpdateForm) {
        Event.fire('orderUpdated', {id: this.model.id, cart});
      }
    },

    beforeCreateHook() {
      if (!this.customer) {
        this.errorMissingCustomer();
        return false;
      }
      if (!this.cart.products.length) {
        this.errorMissingProducts();
        return false;
      }
      return true;
    },

    errorMissingCustomer() {
      this.$notify({
        type: 'error',
        title: 'Erreur',
        text: 'Vous devez sélectionner un client.',
      });
    },

    errorMissingProducts() {
      this.$notify({
        type: 'error',
        title: 'Erreur',
        text: 'Votre commande ne contient pas de produits.',
      });
    },

    getCustomerCart(customer) {
      axios
          .get(route('api.carts.show', customer.id))
          .then(({data}) => this.refreshCart(data.data))
          .catch(error => new ErrorHandler(error).dispatch());
    },

    handleAddToCart() {
      if (!this.customer) {
        return this.errorMissingCustomer();
      }
      return this.$modal.open('cart-product', this.cart);
    },

    afterSuccessHook() {
      if (this.isCartModel) {
        this.$modal.close();
      }
      this.customer = null;
    },
  },
};
</script>